import { z } from 'zod';
import { CONTRACT_TYPES, LEAVE_TYPES, LEAVE_STATUSES, MARITAL_STATUSES, WORK_KINDS, isHourly, } from '../enums.js';
import { optionalText, patchSchemaOf } from './common.js';
/**
 * HR: staff records, leave and timesheets — DECISIONS.md §13.
 *
 * No salary and no payslips anywhere in here, deliberately. MEFOSA's
 * accountant runs payroll outside this system, the same reasoning that keeps
 * VAT out (rule 4), and a half-built payroll is worse than none.
 */
// ------------------------------------------------------------ staff record
export const staffRecordInputSchema = z.object({
    userId: z.string().uuid(),
    /**
     * The employee's name as it appears on their papers.
     *
     * The account carries a DISPLAY name, picked so colleagues recognise a
     * login — MEFOSA's read "Sales 1" through "Sales 4". That is not a name to
     * put on an NSSF declaration, and the two are allowed to differ: a legal
     * name carries a father's name and a family name an account never shows.
     *
     * Optional. Left empty, the screens fall back to the account's name, so no
     * existing record had to be filled in before it could be read.
     */
    employeeName: optionalText,
    employeeNameAr: optionalText,
    jobTitle: optionalText,
    department: optionalText,
    contractType: z.enum(CONTRACT_TYPES).default('full_time'),
    startedOn: z.string().date(),
    endedOn: z.string().date().nullable().optional(),
    /**
     * Working days a year. Lebanese law says 15 after a year of service; MEFOSA
     * gives some people more, so it is per person rather than a constant.
     *
     * Capped at 365 because a typo of 150 for 15 would otherwise produce a
     * balance nobody questions until somebody takes five months off.
     */
    leaveEntitlementDays: z.number().int().min(0).max(365).default(15),
    /**
     * What a working day is, for turning an hour of permission into part of one.
     *
     * Per person: a part-timer does not work the same day as everybody else, and
     * charging their two hours as a quarter of a full day would take more from
     * their balance than they actually took off.
     */
    workingHoursPerDay: z.number().positive().max(24).multipleOf(0.25).default(8),
    personalPhone: optionalText,
    personalEmail: z.union([z.string().trim().toLowerCase().email(), z.literal('')]).optional(),
    address: optionalText,
    dateOfBirth: z.string().date().nullable().optional(),
    /** The ID card or Emirates ID — NOT the NSSF number, which is separate. */
    nationalId: optionalText,
    /**
     * Lebanese social security (الضمان الاجتماعي), recorded by HR.
     *
     * Facts only. The employer registers and declares each employee, and
     * these are the fields that declaration turns on. No contribution is
     * calculated anywhere: the rates change, they split employer/employee
     * across three branches, and payroll lives with the accountant (§2, §13).
     */
    nssfNumber: optionalText,
    nssfRegisteredOn: z.string().date().nullable().optional(),
    maritalStatus: z.enum(MARITAL_STATUSES).nullable().optional(),
    /** Dependent children, for NSSF family allowances. */
    dependents: z.number().int().min(0).max(20).default(0),
    emergencyContactName: optionalText,
    emergencyContactPhone: optionalText,
    notes: optionalText,
});
/** userId is omitted: moving a record to another person is not an edit. */
export const staffRecordUpdateSchema = patchSchemaOf(staffRecordInputSchema).omit({ userId: true });
// ------------------------------------------------------------------- leave
export const leaveRequestInputSchema = z
    .object({
    userId: z.string().uuid(),
    type: z.enum(LEAVE_TYPES).default('annual'),
    startsOn: z.string().date(),
    endsOn: z.string().date(),
    /**
     * WORKING days, entered by a person rather than derived from the dates.
     *
     * A request spanning a weekend or a public holiday is not the number of
     * calendar days between its ends, and working it out would need a Lebanese
     * holiday calendar this system does not have. Guessing would be worse than
     * asking: it would quietly overcharge somebody's balance.
     *
     * Halves are real — a half day off is the commonest request of all.
     */
    days: z.number().min(0).max(365).multipleOf(0.5).default(0),
    /**
     * Hours, for a permission — an hour or two off rather than a day.
     *
     * A permission is quantified in hours and `days` stays 0; everything else
     * is quantified in days and `hours` stays null. The day-equivalent of a
     * permission is DERIVED from the person's working day, never stored, so
     * changing that figure cannot leave old rows disagreeing with it.
     */
    hours: z.number().positive().max(24).multipleOf(0.25).nullable().optional(),
    reason: optionalText,
})
    .refine((v) => v.startsOn <= v.endsOn, {
    message: 'Leave cannot end before it starts',
    path: ['endsOn'],
})
    .refine((v) => (isHourly(v.type) ? (v.hours ?? 0) > 0 : true), {
    message: 'A permission is recorded in hours',
    path: ['hours'],
})
    .refine((v) => (isHourly(v.type) ? true : v.days > 0), {
    message: 'Say how many working days this is',
    path: ['days'],
});
/**
 * Editing a request. Status is NOT here — approving is its own route with its
 * own permission, so a PATCH cannot be used to grant your own leave.
 */
export const leaveRequestUpdateSchema = z.object({
    type: z.enum(LEAVE_TYPES).optional(),
    startsOn: z.string().date().optional(),
    endsOn: z.string().date().optional(),
    days: z.number().min(0).max(365).multipleOf(0.5).optional(),
    hours: z.number().positive().max(24).multipleOf(0.25).nullable().optional(),
    reason: optionalText,
});
/** Granting or refusing. Separate permission, separate route, separate act. */
export const leaveDecisionSchema = z.object({
    status: z.enum(['approved', 'rejected', 'cancelled']),
    decisionNote: optionalText,
});
/**
 * Entitlement minus approved annual leave.
 *
 * Pending requests are deliberately NOT subtracted. A balance that moved when
 * somebody merely asked would let anyone reduce their own on paper, and the
 * screen shows pending beside it so the reader can do that arithmetic
 * themselves if they want to.
 */
export function remainingLeave(entitlementDays, takenDays) {
    // Can go negative, and should: unpaid overrun is a real situation and
    // clamping it to zero would hide exactly the case HR needs to see.
    return Math.round((entitlementDays - takenDays) * 10) / 10;
}
// -------------------------------------------------------------- timesheets
export const timesheetInputSchema = z.object({
    userId: z.string().uuid(),
    workedOn: z.string().date(),
    /**
     * Capped at 24 for the obvious reason, and at quarter-hour granularity,
     * which is as fine as anybody actually records.
     */
    hours: z.number().positive().max(24).multipleOf(0.25),
    kind: z.enum(WORK_KINDS).default('consultancy'),
    billable: z.boolean().default(false),
    companyId: z.string().uuid().nullable().optional(),
    projectId: z.string().uuid().nullable().optional(),
    note: optionalText,
});
export const timesheetUpdateSchema = patchSchemaOf(timesheetInputSchema).omit({ userId: true });
/**
 * The billable share, guarding the division.
 *
 * Zero hours is a real state — somebody who filed nothing — and it must read
 * as 0%, not NaN, which is what reaches the screen if this is done inline.
 */
export function billablePercent(totalHours, billableHours) {
    if (totalHours <= 0)
        return 0;
    return Math.round((billableHours / totalHours) * 1000) / 10;
}
export { LEAVE_STATUSES };
//# sourceMappingURL=hr.js.map